/**
 * R7 AI v1 client. Every AI button checks useAiEnabled() first: false when
 * the server has AI off, no key, or the provider just failed (GET /health
 * ai_enabled) -- and any 503/429 from an AI call hides the button too.
 */
import { useEffect, useState } from 'react';
import apiClient from './api';
import { friendlyApiError } from './vendorDashboard';

let statusPromise = null;
let disabledLocally = false;
const listeners = new Set();

export function fetchAiEnabled({ refresh = false } = {}) {
  if (!statusPromise || refresh) {
    statusPromise = apiClient
      .get('/health')
      .then((res) => Boolean(res.data && res.data.ai_enabled))
      .catch(() => false);
  }
  return statusPromise.then((on) => on && !disabledLocally);
}

/** Called after an AI call fails as "unavailable": hide AI buttons for this page session. */
export function markAiUnavailable() {
  disabledLocally = true;
  listeners.forEach((fn) => fn(false));
}

export function useAiEnabled() {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchAiEnabled().then((on) => { if (alive) setEnabled(on); });
    listeners.add(setEnabled);
    return () => { alive = false; listeners.delete(setEnabled); };
  }, []);
  return enabled;
}

function aiFailure(error, fallback) {
  const status = error.response?.status;
  if (status === 503) markAiUnavailable();
  return { success: false, status, error: friendlyApiError(error, fallback) };
}

/** Shrinks a photo in the browser (<= maxPx, JPEG) before it is sent. */
export function shrinkImage(file, maxPx = 1024, quality = 0.82) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

export const AiService = {
  async draftListing({ hint, categoryId, files }) {
    const form = new FormData();
    form.append('hint', hint);
    if (categoryId) form.append('category_id', String(categoryId));
    const shrunk = await Promise.all((files || []).slice(0, 4).map((f) => shrinkImage(f)));
    shrunk.forEach((blob, i) => form.append('images', blob, `photo-${i + 1}.jpg`));
    try {
      const res = await apiClient.post('/vendor/ai/listing-draft', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      return { success: true, draft: res.data };
    } catch (error) {
      return aiFailure(error, 'The AI assistant could not write a draft.');
    }
  },

  async smartSearch(q, limit = 20) {
    try {
      const res = await apiClient.get('/search/smart', { params: { q, limit } });
      return { success: true, ...res.data };
    } catch (error) {
      return { success: false, results: [], intent: null };
    }
  },

  async reviewSummary(productId) {
    try {
      const res = await apiClient.get(`/products/${productId}/review-summary`);
      return { success: true, summary: res.data };
    } catch (error) {
      return { success: false, summary: null };
    }
  },

  async usage() {
    try {
      const res = await apiClient.get('/admin/ai/usage');
      return { success: true, usage: res.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Could not load AI usage') };
    }
  },

  async imageCheck(productId) {
    try {
      const res = await apiClient.post(`/admin/products/${productId}/ai-image-check`);
      return { success: true, product: res.data };
    } catch (error) {
      return aiFailure(error, 'The AI check could not run.');
    }
  },
};

export const CodConfirmService = {
  async confirmByToken(token) {
    try {
      const res = await apiClient.post('/orders/cod-confirm', { token });
      return { success: true, ...res.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Could not confirm this order.') };
    }
  },

  async confirmSignedIn(orderId) {
    try {
      const res = await apiClient.post(`/orders/${orderId}/cod-confirm`);
      return { success: true, ...res.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Could not confirm this order.') };
    }
  },
};
